/**
 * Shared "save a CatalogOptionsEditor result against the assignees
 * subcollection" helper. Used by both the WBS in-canvas 担当 editor and
 * the Settings page chip-click editor so the two surfaces share
 * identical semantics: delete-guard, rename, color-index update, and
 * brand-new add.
 *
 * Defence-in-depth: the editor's open-time `usageCounts` snapshot can
 * go stale if a parallel writer attaches an item to an assignee between
 * dialog open and save. The `usageById` argument here is the *live*
 * count taken at save time; we refuse deletes whose target is in use
 * before any write fires.
 */
import type { Firestore } from 'firebase/firestore';
import type { AssigneeDoc, ProjectDoc } from '@mct/shared';
import {
  createAssignee,
  deleteAssignee,
  renameAssignee,
  updateAssigneeColorIndex,
} from './assigneeOps';
import type { CatalogOptionEditResult } from '../components/Wbs/CatalogOptionsEditor';

export async function applyAssigneeCatalogEdit(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  originalAssignees: readonly AssigneeDoc[],
  usageById: ReadonlyMap<string, number>,
  result: CatalogOptionEditResult,
): Promise<void> {
  const originalById = new Map(
    originalAssignees.map((a) => [a.assigneeId, a] as const),
  );
  // Pre-flight: refuse deletes whose target is in use right now. The
  // editor disables the trash for in-use rows at open time, but the
  // live count may have advanced since.
  for (const [id, target] of result.renames) {
    if (target !== null) continue;
    const orig = originalById.get(id);
    const live = usageById.get(id) ?? 0;
    if (orig && live > 0) {
      throw new Error(
        `「${orig.name}」は ${live} 件のタスクで使用中のため削除できません`,
      );
    }
  }
  // 1. Apply rename / delete diffs.
  for (const [id, target] of result.renames) {
    const orig = originalById.get(id);
    if (!orig) continue;
    if (target === null) {
      await deleteAssignee(db, project.projectId, id);
    } else if (target !== orig.name) {
      await renameAssignee(db, project.projectId, id, target);
    }
  }
  // 2. Apply color-index changes for kept rows. The editor doesn't
  //    diff colors itself, so we walk every option and write the
  //    colorIndex if it differs from the doc's current value.
  for (const opt of result.options) {
    const orig = originalById.get(opt.id);
    if (!orig) continue; // new rows handled in step 3
    if (
      typeof opt.colorIndex === 'number' &&
      opt.colorIndex !== orig.colorIndex
    ) {
      await updateAssigneeColorIndex(
        db,
        project.projectId,
        opt.id,
        opt.colorIndex,
      );
    }
  }
  // 3. Brand-new rows: any option whose `id` is not in `originalById`
  //    (the editor's save derivation graduates `__new_*` ids to the
  //    label-derived id).
  for (const opt of result.options) {
    if (originalById.has(opt.id)) continue;
    await createAssignee(db, project, {
      name: opt.label,
      colorIndex: opt.colorIndex,
    });
  }
}
